/**
 * @fileoverview Confirmation dialog for clearing previously migrated Streamline legacy data, either for one
 *               dataset tab or for every tab of the selected department.
 * @module system-web/components/migration/ClearMigratedDataModal
 *
 * Responsibilities:
 * - Show the target of the clear (current tab or whole selected department) and the execution path resolved
 *   from the browser host.
 * - List every dependent tab the server will also clear, so the admin is informed before confirming.
 * - Ask for explicit confirmation before the destructive action.
 *
 * Dependencies:
 * - `react` (`useEffect`).
 * - `lucide-react` (`Trash2`, `AlertTriangle`, `Loader2`, `X`).
 * - `../../services/legacyMigrationService` (`ClearMigrationScope`, `ClearMigrationTenantSummary`).
 *
 * Architecture:
 * - Presentational. The dependency list comes from the server dry-run response (`clearedDatasetKeys`); the
 *   dependency graph is NOT duplicated in the UI.
 *
 * Notes:
 * - Confirming closes the dialog immediately. Progress and the result are shown by `LegacyMigrationPage` in a
 *   non-blocking status card, so the rest of the page stays usable while the clear runs.
 */

import React, { useEffect } from 'react';
import { Trash2, AlertTriangle, Loader2, X } from 'lucide-react';
import type { ClearMigrationScope, ClearMigrationTenantSummary } from '../../services/legacyMigrationService';

/**
 * @contract ClearMigratedDataModalProps
 * Purpose: Props for `ClearMigratedDataModal`.
 * Fields: scope, departmentName, legacyDeptId, activeDatasetKey, activeDatasetDisplayName, datasetLabels,
 *         executionLabel, previewSummaries, isPreviewLoading, previewError, onConfirm, onCancel.
 * Rules: Confirm is disabled while the dry-run preview is loading or unavailable.
 * Sync: Presentational props driven by `LegacyMigrationPage` state.
 */
export interface ClearMigratedDataModalProps {
  scope: ClearMigrationScope;
  departmentName: string;
  legacyDeptId?: string;
  activeDatasetKey: string;
  activeDatasetDisplayName: string;
  datasetLabels: Record<string, string>;
  executionLabel: string;
  previewSummaries: ClearMigrationTenantSummary[] | null;
  isPreviewLoading: boolean;
  previewError: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * @component ClearMigratedDataModal
 * Purpose: Confirmation dialog listing the tabs that will be cleared before a destructive clear runs.
 * Props: `ClearMigratedDataModalProps`.
 * Behavior: Shows target, resolved execution path, and dependent tabs reported by the server; Cancel or Confirm.
 * Data Source: Hybrid (dependent tab keys come from the online dry-run request, held in page state).
 * Constraints: Never calls `fetch` or `apiFetch` directly.
 * Performance: Lightweight modal; no unbounded lists.
 * @reusable
 * Usage: Rendered by `LegacyMigrationPage` from "Remove Data" and "Clear Department Data".
 * Rules: Presentation-only; all state and callbacks passed via props.
 */
export function ClearMigratedDataModal({
  scope,
  departmentName,
  legacyDeptId,
  activeDatasetKey,
  activeDatasetDisplayName,
  datasetLabels,
  executionLabel,
  previewSummaries,
  isPreviewLoading,
  previewError,
  onConfirm,
  onCancel,
}: ClearMigratedDataModalProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const clearedKeys = Array.from(new Set((previewSummaries ?? []).flatMap((s) => s.clearedDatasetKeys)));
  const isDepartmentScope = scope === 'department-all';
  const dependentLabels = clearedKeys
    .filter((key) => key !== activeDatasetKey)
    .map((key) => datasetLabels[key] ?? key);

  const title = isDepartmentScope
    ? `Clear All Migrated Data: ${departmentName}`
    : `Clear Migrated Data: ${activeDatasetDisplayName}`;
  const confirmLabel = isDepartmentScope ? 'Clear All Tabs' : `Clear ${activeDatasetDisplayName}`;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="clear-migrated-modal-title"
    >
      <div
        className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 relative max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close dialog"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2.5 mb-2 pr-6">
          <div className="p-2 rounded-lg bg-red-50 text-red-600">
            <Trash2 className="h-5 w-5" />
          </div>
          <div>
            <h2 id="clear-migrated-modal-title" className="text-lg font-semibold text-gray-900">
              {title}
            </h2>
            <p className="text-xs text-gray-500">
              {departmentName}
              {legacyDeptId ? ` · Dept ${legacyDeptId}` : ''} · Runs via {executionLabel}
            </p>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 space-y-4 pr-1 my-2">
          {isPreviewLoading && (
            <div className="inline-flex items-center gap-1.5 text-xs text-gray-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Checking which tabs will be affected…</span>
            </div>
          )}

          {previewError && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2.5 py-1.5">
              {previewError}
            </div>
          )}

          {!isPreviewLoading && !previewError && previewSummaries && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 space-y-1.5">
              <div className="font-semibold flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                <span>
                  {isDepartmentScope
                    ? `All ${clearedKeys.length} migrated tabs for this department will be cleared.`
                    : dependentLabels.length > 0
                      ? `${dependentLabels.length} dependent tab(s) will also be cleared.`
                      : 'Only this tab will be cleared.'}
                </span>
              </div>
              {!isDepartmentScope && dependentLabels.length > 0 && (
                <>
                  <p>
                    Their migrated records are embedded in, or reference, the data removed from this tab, so they are reset
                    to keep the migration order consistent:
                  </p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {dependentLabels.map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                  </ul>
                </>
              )}
              <p className="text-amber-800">
                Migrated records cannot be restored automatically, and manual edits to them are removed too.
                System-seeded baseline lookups, users, and roles are preserved.
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPreviewLoading || !previewSummaries}
            className="px-4 py-2 text-sm rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            <span>{confirmLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
